import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { IconButton } from '@ui/buttons';
import { GhostButton } from '@ui/GhostButton';
import { Icon } from '@ui/Icon';
import { Menu } from '@ui/menus/Menu';
import { SidePanel } from '@ui/layout';
import { PANEL_CONTENT_DEFAULT_SPACING } from '@ui/layout/panels/panel.constants';
import { Slider } from '@ui/Slider';
import { UnitNumberInput } from '@ui/UnitNumberInput';
import {
  formatBlueprintPaperSizeLabel,
  getBlueprintDrawingScaleRatio,
  getEffectivePaperSizeMm,
  getStoreyLevel,
  matchBlueprintIsoPaperName,
  patchBlueprintDrawingScale,
  patchBlueprintIsoPaperSize,
  removeBlueprintUnderlaysShownForBuilding,
  resolveBlueprintUnderlayStoreySpans,
  clampBlueprintOpacity,
  updateBlueprintUnderlayById,
  type BlueprintIsoPaperName,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import { formatPdfPageHeading } from '@shared/helpers/design/pdf-page-heading.helpers';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import { reportDesignPersistError } from '../helpers/design-snapshot-persist.helpers';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { menuMouseHandler, openMenu } from '@client/hooks/use-menu.hooks';
import {
  persistBlueprintUnderlays,
  useActiveBlueprintUnderlay,
  useBlueprintUnderlayBuildingId,
  useBlueprintUnderlays,
  useUpdateBuildingDesign,
} from '../hooks/design.hooks';
import { useBlueprintUnderlaysWithRasters } from '../hooks/blueprint-underlay-rasters.hook';
import { selectBlueprintUnderlayIdsToHydrate } from '../helpers/blueprint-underlay-raster-hydration.helpers';
import { useStoreys } from '../hooks/storeys.hooks';
import { useBlueprintUnderlayPageActions } from '../hooks/use-blueprint-underlay-page-actions.hook';
import { blueprintUnderlayPageMenuId } from '../helpers/blueprint-underlay-page-menu.helpers';
import { BlueprintPaperSizeSelect } from './BlueprintPaperSizeSelect';
import type { BlueprintOpacityPreview } from '../helpers/blueprint-opacity-preview.helpers';
import {
  useBlueprintInteractionMode,
  useRequestBlueprintImport,
  useSelectedStoreyId,
  useSetBlueprintInteractionMode,
  useSetBlueprintOpacityPreview,
  useSetCalibratePoints,
  useSetManualBlueprintUnderlayId,
} from '../store/design-store.hooks';
import { guards } from '@shared/helpers/typeguards.helpers';

type BlueprintOpacitySliderProps = {
  pageId: string;
  value: number;
  onChange: (pageId: string, opacity: number) => void;
  onChangeCommitted: (pageId: string, opacity: number) => void;
  onDismiss: (pageId: string) => void;
};

/**
 * Opacity slider for one underlay page.
 * Unmount drops an unsaved drag so the map does not keep that preview.
 */
const BlueprintOpacitySlider: React.FC<BlueprintOpacitySliderProps> = ({
  pageId,
  value,
  onChange,
  onChangeCommitted,
  onDismiss,
}) => {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  useEffect(() => {
    return () => {
      onDismissRef.current(pageId);
    };
  }, [pageId]);

  return (
    <Slider
      min={0.1}
      max={1}
      step={0.05}
      value={value}
      onChange={(_, next) => {
        onChange(pageId, next);
      }}
      onChangeCommitted={(_, next) => {
        void onChangeCommitted(pageId, next);
      }}
    />
  );
};

/** Rounded `1:N` draft for an underlay, or undefined when scale is unknown. */
const scaleDraftFromUnderlay = (
  underlay: BlueprintUnderlay,
): number | undefined => {
  const ratio = getBlueprintDrawingScaleRatio(underlay);
  if (ratio === undefined || !Number.isFinite(ratio) || !(ratio > 0)) {
    return undefined;
  }
  return Math.round(ratio);
};

/** Build per-page scale drafts from the current underlay list. */
const scaleDraftsFromUnderlays = (
  underlays: readonly BlueprintUnderlay[],
): Record<string, number | undefined> =>
  Object.fromEntries(
    underlays.map((page) => [page.id, scaleDraftFromUnderlay(page)]),
  );

/**
 * Drafts from the stored list, keeping pages the user has typed.
 * A move or opacity save can publish a new list while confirm is open;
 * replacing those drafts would show the previous `1:N` in the field.
 */
const scaleDraftsPreservingTyped = (
  storedDrafts: Record<string, number | undefined>,
  previousDrafts: Record<string, number | undefined>,
  typedPageIds: Record<string, true>,
): Record<string, number | undefined> => {
  const next: Record<string, number | undefined> = {};
  for (const pageId of Object.keys(storedDrafts)) {
    next[pageId] = typedPageIds[pageId]
      ? previousDrafts[pageId]
      : storedDrafts[pageId];
  }
  return next;
};

/**
 * Design right-panel for PDF underlay calibration and placed pages.
 */
export const BlueprintCalibrationPanel: React.FC = () => {
  const { classes, cx } = useStyles();
  const underlay = useActiveBlueprintUnderlay();
  const storedUnderlays = useBlueprintUnderlays();
  const buildingId = useBlueprintUnderlayBuildingId();
  const storeys = useStoreys();
  const updateBuildingDesign = useUpdateBuildingDesign();
  const errorHandler = useErrorHandler();
  const confirm = useIsConfirmed();
  const setManualUnderlayId = useSetManualBlueprintUnderlayId();
  const mode = useBlueprintInteractionMode();
  const setMode = useSetBlueprintInteractionMode();
  const setCalibratePoints = useSetCalibratePoints();
  const requestBlueprintImport = useRequestBlueprintImport();
  const { isShownOnMap, toggleShownOnMap } = useBlueprintUnderlayPageActions();
  const [pagesMenuAnchor, setPagesMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const selectedStoreyId = useSelectedStoreyId();
  const selectedStoreyLevel = getStoreyLevel(storeys, selectedStoreyId);
  /** Missing/undefined means collapsed; only explicitly `false` is open (storey panel pattern). */
  const [collapsedPages, setCollapsedPages] = useState<Record<string, boolean>>(
    {},
  );
  const [scaleDrafts, setScaleDrafts] = useState<
    Record<string, number | undefined>
  >(() => scaleDraftsFromUnderlays(storedUnderlays));
  /** Pages whose scale field the user has typed into. Stamps `drawingScaleSource` as `manual`. */
  const [scaleTypedIds, setScaleTypedIds] = useState<Record<string, true>>({});
  // UnitNumberInput commits via onChange then onBlur in the same tick —
  // blur handlers must read the draft from a ref, not stale render state.
  const scaleDraftsRef = useRef(scaleDrafts);
  scaleDraftsRef.current = scaleDrafts;
  const scaleTypedIdsRef = useRef(scaleTypedIds);
  scaleTypedIdsRef.current = scaleTypedIds;
  /** Opacity shown while a slider drag has not been saved yet. */
  const [opacityDrafts, setOpacityDrafts] = useState<Record<string, number>>(
    {},
  );
  // Keep the latest drag here so a save that finishes late does not wipe a newer drag.
  const opacityDraftsRef = useRef(opacityDrafts);
  const opacityPreviewRef = useRef<BlueprintOpacityPreview | undefined>(
    undefined,
  );
  /** Bumped on each drag tick so a finished save cannot clear a later drag. */
  const opacityDragGenerationRef = useRef<Record<string, number>>({});
  const setBlueprintOpacityPreview = useSetBlueprintOpacityPreview();

  // Leaving the panel drops a drag the slider never committed.
  useEffect(() => {
    return () => {
      const generations = opacityDragGenerationRef.current;
      const invalidated: Record<string, number> = {};
      for (const pageId of Object.keys(generations)) {
        invalidated[pageId] = (generations[pageId] ?? 0) + 1;
      }
      opacityDragGenerationRef.current = invalidated;
      opacityPreviewRef.current = undefined;
      opacityDraftsRef.current = {};
      setBlueprintOpacityPreview(undefined);
    };
  }, [setBlueprintOpacityPreview]);

  useEffect(() => {
    setScaleDrafts((previous) =>
      scaleDraftsPreservingTyped(
        scaleDraftsFromUnderlays(storedUnderlays),
        previous,
        scaleTypedIdsRef.current,
      ),
    );
  }, [storedUnderlays]);

  const openDetailUnderlayIds = useMemo(
    () =>
      storedUnderlays
        .filter((page) => collapsedPages[page.id] === false)
        .map((page) => page.id),
    [collapsedPages, storedUnderlays],
  );
  const rasterHydrateIds = useMemo(
    () =>
      selectBlueprintUnderlayIdsToHydrate({
        underlayIds: storedUnderlays.map((page) => page.id),
        activeUnderlayId: underlay?.id,
        openDetailUnderlayIds,
      }),
    [openDetailUnderlayIds, storedUnderlays, underlay?.id],
  );
  const underlays = useBlueprintUnderlaysWithRasters(
    storedUnderlays,
    rasterHydrateIds,
  );

  /**
   * Pages covering the selected storey — a page covers exactly the storeys it
   * is assigned to. The active page always stays listed so a manual page
   * override never empties the list.
   */
  const pagesForSelectedStorey = useMemo(() => {
    if (selectedStoreyLevel === undefined) return underlays;
    const spans = resolveBlueprintUnderlayStoreySpans(underlays, storeys);
    return underlays.filter((page) => {
      if (page.id === underlay?.id) return true;
      const span = spans.get(page.id);
      if (!span) return false;
      return (
        selectedStoreyLevel >= span.fromLevel &&
        selectedStoreyLevel <= span.toLevel
      );
    });
  }, [selectedStoreyLevel, storeys, underlay?.id, underlays]);

  // Open the shown page's detail when it changes from the map / Show menu.
  useEffect(() => {
    const shownId = underlay?.id;
    if (!shownId) return;
    setCollapsedPages((prev) => ({ ...prev, [shownId]: false }));
  }, [underlay?.id]);

  /**
   * Toggle one page's detail. Pages expand independently — the eye, not the
   * open row, decides which page the map draws.
   * A missing flag is collapsed, so the first click opens the row and starts
   * its thumbnail fetch.
   */
  const handleTogglePageDetail = useCallback((pageId: string) => {
    setCollapsedPages((prev) => ({
      ...prev,
      // Open (`false`) collapses; missing or `true` opens.
      [pageId]: prev[pageId] === false,
    }));
  }, []);

  /**
   * Show a slider drag in the panel and on the map without saving the design.
   */
  const previewOpacity = useCallback(
    (pageId: string, opacity: number) => {
      const next = clampBlueprintOpacity(opacity);
      const preview: BlueprintOpacityPreview = {
        underlayId: pageId,
        opacity: next,
      };
      opacityDragGenerationRef.current = {
        ...opacityDragGenerationRef.current,
        [pageId]: (opacityDragGenerationRef.current[pageId] ?? 0) + 1,
      };
      opacityDraftsRef.current = {
        ...opacityDraftsRef.current,
        [pageId]: next,
      };
      opacityPreviewRef.current = preview;
      setOpacityDrafts(opacityDraftsRef.current);
      setBlueprintOpacityPreview(preview);
    },
    [setBlueprintOpacityPreview],
  );

  /**
   * Drop a preview when its slider goes away before the drag is saved.
   * A commit that is already in flight still writes opacity on the live page.
   */
  const dismissOpacityDrag = useCallback(
    (pageId: string) => {
      const hasDraft = pageId in opacityDraftsRef.current;
      const ownsPreview = opacityPreviewRef.current?.underlayId === pageId;
      if (!hasDraft && !ownsPreview) return;
      // An in-flight save captured the previous generation and must not clear
      // a drag that starts after this slider is gone.
      opacityDragGenerationRef.current = {
        ...opacityDragGenerationRef.current,
        [pageId]: (opacityDragGenerationRef.current[pageId] ?? 0) + 1,
      };
      if (hasDraft) {
        const { [pageId]: _cleared, ...rest } = opacityDraftsRef.current;
        opacityDraftsRef.current = rest;
        setOpacityDrafts(rest);
      }
      if (!ownsPreview) return;
      opacityPreviewRef.current = undefined;
      setBlueprintOpacityPreview(undefined);
    },
    [setBlueprintOpacityPreview],
  );

  /**
   * Drop a drag preview once that generation has been saved, or the save failed.
   * A later drag bumps the generation and keeps its own preview.
   */
  const clearOpacityDrag = useCallback(
    (pageId: string, generation: number) => {
      if (opacityDragGenerationRef.current[pageId] !== generation) return;
      if (pageId in opacityDraftsRef.current) {
        const { [pageId]: _cleared, ...rest } = opacityDraftsRef.current;
        opacityDraftsRef.current = rest;
        setOpacityDrafts(rest);
      }
      const preview = opacityPreviewRef.current;
      if (preview?.underlayId !== pageId) return;
      opacityPreviewRef.current = undefined;
      setBlueprintOpacityPreview(undefined);
    },
    [setBlueprintOpacityPreview],
  );

  /**
   * Save one page's opacity after the slider is released.
   * Intermediate drag ticks only update {@link previewOpacity}.
   */
  const commitOpacity = useCallback(
    async (pageId: string, opacity: number) => {
      const nextOpacity = clampBlueprintOpacity(opacity);
      const generation = opacityDragGenerationRef.current[pageId] ?? 0;
      try {
        // Patch the list locked at save time so a move during the PUT keeps
        // its pose. Only opacity comes from this slider.
        await persistBlueprintUnderlays((live) => {
          const current = live.find((entry) => entry.id === pageId);
          if (!current || current.opacity === nextOpacity) return live;
          return updateBlueprintUnderlayById(live, {
            ...current,
            opacity: nextOpacity,
          });
        }, updateBuildingDesign);
      } catch (error: unknown) {
        reportDesignPersistError(error, errorHandler);
      } finally {
        clearOpacityDrag(pageId, generation);
      }
    },
    [clearOpacityDrag, errorHandler, updateBuildingDesign],
  );

  /**
   * Commit manual `1:N` scale for one page — resizes the PDF and updates
   * calibrated Length. With multiple pages, ask this page vs all pages.
   */
  const commitDrawingScale = useCallback(
    async (pageId: string, ratio: number | undefined) => {
      const clearScaleTyped = (id: string) => {
        if (!scaleTypedIdsRef.current[id]) return;
        const { [id]: _removed, ...rest } = scaleTypedIdsRef.current;
        scaleTypedIdsRef.current = rest;
        setScaleTypedIds(rest);
      };
      const page = underlays.find((entry) => entry.id === pageId);
      if (!page) return;

      if (ratio === undefined || !(ratio > 0) || !Number.isFinite(ratio)) {
        const restored = scaleDraftFromUnderlay(page);
        setScaleDrafts((prev) => ({ ...prev, [pageId]: restored }));
        scaleDraftsRef.current = {
          ...scaleDraftsRef.current,
          [pageId]: restored,
        };
        clearScaleTyped(pageId);
        return;
      }

      const storedRatio = getBlueprintDrawingScaleRatio(page);
      // Blur fires even when the digits did not change. Asking then would
      // steal the next click, and "All pages" would rewrite every sheet.
      if (
        guards.is.finiteNumber(storedRatio) &&
        storedRatio > 0 &&
        Math.round(storedRatio) === Math.round(ratio)
      ) {
        clearScaleTyped(pageId);
        return;
      }

      let scope: 'active' | 'all' = 'active';
      if (underlays.length > 1) {
        const applyAll = await confirm({
          title: 'Apply scale',
          description:
            'Apply this drawing scale to all blueprint pages, or only this page?',
          confirmationText: 'All pages',
          cancellationText: 'This page',
        });
        scope = applyAll ? 'all' : 'active';
      }

      // Read the typed flag from the ref — UnitNumberInput fires onChange then
      // onBlur in the same tick, before React re-renders state into the ref.
      const userTypedScale = Boolean(scaleTypedIdsRef.current[pageId]);

      try {
        // Patch the list locked at save time so a move or new page during
        // the confirm dialog is not replaced by this render's snapshot.
        await persistBlueprintUnderlays(
          (live) =>
            patchBlueprintDrawingScale(live, {
              pageId,
              ratio,
              scope,
              buildingId,
              userTypedScale,
            }),
          updateBuildingDesign,
        );
      } catch (error: unknown) {
        reportDesignPersistError(error, errorHandler);
      } finally {
        setScaleTypedIds((prev) => {
          if (!prev[pageId]) return prev;
          const { [pageId]: _cleared, ...rest } = prev;
          return rest;
        });
      }
    },
    [buildingId, confirm, errorHandler, underlays, updateBuildingDesign],
  );

  /**
   * Change ISO paper size for one page — keeps `1:N`, resizes the map underlay.
   */
  const commitPaperSize = useCallback(
    async (pageId: string, name: BlueprintIsoPaperName) => {
      try {
        // Patch the locked list so a queued map move keeps its new center.
        await persistBlueprintUnderlays(
          (live) => patchBlueprintIsoPaperSize(live, pageId, name),
          updateBuildingDesign,
        );
      } catch (error: unknown) {
        reportDesignPersistError(error, errorHandler);
      }
    },
    [errorHandler, updateBuildingDesign],
  );

  /**
   * Reset PDF selection chrome after the underlay list becomes empty.
   */
  const clearPdfSelectionChrome = useCallback(() => {
    setMode('locked');
    setCalibratePoints([]);
    setManualUnderlayId(undefined);
  }, [setCalibratePoints, setManualUnderlayId, setMode]);

  /** Finish working with the underlays — deselect and leave measure mode. */
  const handleDone = useCallback(() => {
    setCalibratePoints([]);
    setMode('locked');
    setManualUnderlayId(undefined);
  }, [setCalibratePoints, setManualUnderlayId, setMode]);

  /**
   * Open the page kebab / context menu at the pointer.
   */
  const openPageMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, pageId: string) => {
      const position = menuMouseHandler(event);
      if (!position) return;
      openMenu({
        anchorPosition: position,
        id: blueprintUnderlayPageMenuId(pageId),
      });
    },
    [],
  );

  /**
   * Remove every placed PDF page after confirm.
   */
  const handleRemoveAllPages = useCallback(async () => {
    setPagesMenuAnchor(null);
    if (!underlays.length) return;
    // While no building is selected the list shows the site plans.
    const sitePlans = underlays.every((page) => page.site === true);
    const confirmed = await confirm({
      title: 'Delete all PDF pages',
      description: sitePlans
        ? 'Remove every site plan page? This cannot be undone.'
        : 'Remove every blueprint page from this building? This cannot be undone.',
      confirmationText: 'Delete all',
      cancellationText: 'Cancel',
    });
    if (!confirmed) return;
    // Filter the live list after the lock so pages placed during confirm go
    // too, while other buildings keep theirs.
    try {
      await persistBlueprintUnderlays(
        (live) =>
          sitePlans
            ? live.filter((page) => page.site !== true)
            : removeBlueprintUnderlaysShownForBuilding(live, buildingId),
        updateBuildingDesign,
      );
    } catch (error: unknown) {
      reportDesignPersistError(error, errorHandler);
      return;
    }
    clearPdfSelectionChrome();
  }, [
    buildingId,
    clearPdfSelectionChrome,
    confirm,
    errorHandler,
    underlays,
    updateBuildingDesign,
  ]);

  /** Always open the OS file picker — never reuse a cached PDF session. */
  const handleAddPages = useCallback(() => {
    requestBlueprintImport('add-pdf');
  }, [requestBlueprintImport]);

  if ((mode !== 'calibrate' && mode !== 'transform') || !underlay) {
    return null;
  }

  return (
    <>
      <SidePanel.Content.Section>
        <Box className={classes.sectionHeader}>
          <Box className={classes.sectionHeaderTitle}>
            <span className={classes.pdfPagesHeading}>Underlays</span>
          </Box>
          <Box className={classes.sectionHeaderActions}>
            <Tooltip title="Add underlays">
              <IconButton
                category="mui"
                icon="Add"
                size="small"
                shape="square"
                aria-label="Add underlays"
                onClick={handleAddPages}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              />
            </Tooltip>
            <Tooltip title="Underlay options">
              <IconButton
                category="mui"
                icon="MoreVert"
                size="small"
                shape="square"
                aria-label="Underlays options"
                aria-haspopup="menu"
                aria-expanded={Boolean(pagesMenuAnchor)}
                onClick={(event) => setPagesMenuAnchor(event.currentTarget)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              />
            </Tooltip>
            <Menu
              open={Boolean(pagesMenuAnchor)}
              anchorEl={pagesMenuAnchor}
              onClose={() => setPagesMenuAnchor(null)}
              width={200}
            >
              <Menu.Item
                icon={<Icon category="mui" icon="Delete" size="small" />}
                onClick={() => void handleRemoveAllPages()}
              >
                Delete all underlays
              </Menu.Item>
            </Menu>
            <Box className={classes.headerDone}>
              <GhostButton onClick={handleDone}>Done</GhostButton>
            </Box>
          </Box>
        </Box>
      </SidePanel.Content.Section>

      <SidePanel.Content.Divider />

      {/* Flush selection chrome to the panel edges (no section inset around the box). */}
      <SidePanel.Content.Section sx={pageListSectionSx}>
        <Box className={classes.pageList}>
          {pagesForSelectedStorey.map((page) => {
            const isDetailOpen = collapsedPages[page.id] === false;
            const rowFileLabel = page.fileName?.trim();
            const shownOnMap = isShownOnMap(page.id);
            const pageLabel = `Page ${page.pageIndex + 1}`;
            const rowTitle = rowFileLabel
              ? `${rowFileLabel} - ${pageLabel}`
              : pageLabel;
            const scaleDraft = scaleDrafts[page.id];
            const paperSizeLabel = formatBlueprintPaperSizeLabel(page);
            const paperSize = getEffectivePaperSizeMm(page);
            const isoPaperName = paperSize
              ? matchBlueprintIsoPaperName(
                  paperSize.widthMm,
                  paperSize.heightMm,
                )
              : undefined;
            return (
              <Box key={page.id} className={classes.pageBlock}>
                <Box
                  className={cx(
                    classes.pageHeader,
                    // Only the row dims when hidden — the detail stays readable.
                    !shownOnMap && classes.pageRowHidden,
                  )}
                  onClick={() => handleTogglePageDetail(page.id)}
                  onContextMenu={(event) => openPageMenu(event, page.id)}
                >
                  <Box className={classes.pageExpandCell}>
                    <IconButton
                      category="mui"
                      icon="KeyboardArrowRight"
                      rotateIcon={isDetailOpen ? 90 : 0}
                      size="small"
                      shape="square"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleTogglePageDetail(page.id);
                      }}
                      aria-label={
                        isDetailOpen
                          ? 'Hide underlay details'
                          : 'Show underlay details'
                      }
                      aria-expanded={isDetailOpen}
                    />
                  </Box>
                  <Box className={classes.pageRowMain}>
                    <Tooltip title={rowTitle}>
                      <Typography
                        variant="body2"
                        className={classes.pageRowLabel}
                      >
                        {rowTitle}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box
                    className={classes.pageActionCell}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Tooltip
                      title={shownOnMap ? 'Hide underlay' : 'Show underlay'}
                    >
                      <IconButton
                        category="mui"
                        // Icon shows the current state: crossed out = hidden.
                        icon={shownOnMap ? 'Visibility' : 'VisibilityOff'}
                        size="small"
                        shape="square"
                        aria-label={
                          shownOnMap ? 'Hide underlay' : 'Show underlay'
                        }
                        aria-pressed={!shownOnMap}
                        onClick={() => void toggleShownOnMap(page.id)}
                      />
                    </Tooltip>
                    <IconButton
                      category="mui"
                      icon="MoreVert"
                      size="small"
                      shape="square"
                      aria-label="Underlay options"
                      aria-haspopup="menu"
                      onClick={(event) => openPageMenu(event, page.id)}
                    />
                  </Box>
                </Box>
                <Box
                  className={cx(
                    classes.pageDetailExpand,
                    isDetailOpen && classes.pageDetailExpandOpen,
                  )}
                >
                  <Box className={classes.pageDetailExpandInner}>
                    {isDetailOpen && (
                      <Box className={classes.pageDetailInset}>
                        {guards.is.nonEmptyString(page.imageDataUrl) ? (
                          <img
                            src={page.imageDataUrl}
                            alt={formatPdfPageHeading(
                              page.pageIndex,
                              page.label,
                            )}
                            className={classes.thumbImage}
                          />
                        ) : (
                          <Box
                            className={classes.thumbPlaceholder}
                            aria-hidden
                          />
                        )}
                        <Box className={classes.fieldStack}>
                          <Box className={classes.fieldRow}>
                            <Typography
                              variant="body2"
                              className={classes.secondaryText}
                            >
                              Scale
                            </Typography>
                            <Box className={classes.scaleRow}>
                              {paperSizeLabel ? (
                                <BlueprintPaperSizeSelect
                                  value={isoPaperName}
                                  label={paperSizeLabel}
                                  aria-label={`Paper size for ${rowTitle}`}
                                  onChange={(name) =>
                                    void commitPaperSize(page.id, name)
                                  }
                                />
                              ) : null}
                              <Box className={classes.scaleRatio}>
                                <Typography
                                  variant="body2"
                                  className={cx(
                                    classes.scalePrefix,
                                    classes.secondaryText,
                                  )}
                                >
                                  1:
                                </Typography>
                                <UnitNumberInput
                                  size="small"
                                  value={scaleDraft}
                                  aria-label={
                                    paperSizeLabel
                                      ? `Drawing scale for ${rowTitle} (${paperSizeLabel})`
                                      : `Drawing scale for ${rowTitle}`
                                  }
                                  onChange={(value) => {
                                    // Keep refs in sync before onBlur in the same tick.
                                    scaleTypedIdsRef.current = {
                                      ...scaleTypedIdsRef.current,
                                      [page.id]: true,
                                    };
                                    scaleDraftsRef.current = {
                                      ...scaleDraftsRef.current,
                                      [page.id]: value,
                                    };
                                    setScaleTypedIds((prev) => ({
                                      ...prev,
                                      [page.id]: true,
                                    }));
                                    setScaleDrafts((prev) => ({
                                      ...prev,
                                      [page.id]: value,
                                    }));
                                  }}
                                  onBlur={() =>
                                    void commitDrawingScale(
                                      page.id,
                                      scaleDraftsRef.current[page.id],
                                    )
                                  }
                                  onKeyDown={(event) => {
                                    if (event.key !== KeyboardKeys.ENTER)
                                      return;
                                    event.preventDefault();
                                    const target = event.target;
                                    if (!(target instanceof HTMLElement))
                                      return;
                                    target.blur();
                                  }}
                                  outlineVisibility="hover"
                                  textAlignment="right"
                                  className={classes.scaleField}
                                />
                              </Box>
                            </Box>
                          </Box>
                          <Box className={classes.opacityBlock}>
                            <Box className={classes.fieldRow}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                                Opacity
                              </Typography>
                              <Typography
                                variant="body2"
                                className={classes.fieldMeta}
                              >
                                {`${Math.round((opacityDrafts[page.id] ?? page.opacity) * 100)}%`}
                              </Typography>
                            </Box>
                            <BlueprintOpacitySlider
                              pageId={page.id}
                              value={opacityDrafts[page.id] ?? page.opacity}
                              onChange={previewOpacity}
                              onChangeCommitted={commitOpacity}
                              onDismiss={dismissOpacityDrag}
                            />
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </SidePanel.Content.Section>
    </>
  );
};

/** Chevron column width — page title / viewer text share this left inset. */
const PAGE_ROW_GUTTER_PX = 32;

/** Shared vertical rhythm in the page viewer (thumb ↔ Opacity). */
const PAGE_DETAIL_GAP = 1.5;

/**
 * Keep default gap under the Underlays header / divider so the page list does
 * not pull into the heading. Cancel only the bottom section gap so the list can
 * sit flush at the panel foot. Horizontal flush is via pageList bleed.
 */
const pageListSectionSx = {
  mb: (theme: { spacing: (n: number) => string }) =>
    theme.spacing(-PANEL_CONTENT_DEFAULT_SPACING),
} as const;

const useStyles = makeStyles()((theme) => ({
  pageDetailInset: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(PAGE_DETAIL_GAP),
    // Indent to the page title (same column as the header text).
    padding: theme.spacing(1.5, 1.5, 2, `${PAGE_ROW_GUTTER_PX}px`),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0, 1, 1),
  },
  sectionHeaderTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  sectionHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing(0.25),
  },
  headerDone: {
    display: 'flex',
    alignItems: 'center',
    // Text button needs more air than the icon-to-icon gap.
    marginLeft: theme.spacing(0.75),
  },
  pdfPagesHeading: {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  fieldStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    // Match name ↔ thumbnail spacing for Scale / Length / Opacity.
    gap: theme.spacing(PAGE_DETAIL_GAP),
  },
  opacityBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(0.75),
    // Breathing room around the label + slider pair.
    padding: theme.spacing(2, 0),
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  fieldMeta: {
    fontVariantNumeric: 'tabular-nums',
    color: theme.palette.text.secondary,
  },
  scaleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    // Air between the paper chip and `1:N`.
    gap: theme.spacing(1.5),
    flexShrink: 0,
  },
  scaleRatio: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexShrink: 0,
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  scalePrefix: {
    // Same weight as the Scale / Opacity labels — not semibold.
    fontVariantNumeric: 'tabular-nums',
  },
  scaleField: {
    // Hug the digits like map segment-length edits (`fieldSizing: content`).
    width: 'fit-content',
    maxWidth: '100%',
    '& .MuiInputBase-root': {
      width: 'fit-content',
      flexGrow: 0,
      paddingLeft: 2,
      paddingRight: 2,
    },
    '& .MuiInputBase-input': {
      // Beat MUI's default `width: 100%` so the field can shrink to the value.
      width: 'auto',
      minWidth: '2ch',
      padding: '0 2px',
      boxSizing: 'content-box',
      color: theme.palette.text.secondary,
      fontVariantNumeric: 'tabular-nums',
      fieldSizing: 'content',
    },
  },
  pageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    // Bleed past section padding so selection chrome is edge-to-edge.
    width: `calc(100% + 2 * ${theme.spacing(PANEL_CONTENT_DEFAULT_SPACING)})`,
    marginLeft: theme.spacing(-PANEL_CONTENT_DEFAULT_SPACING),
    marginRight: theme.spacing(-PANEL_CONTENT_DEFAULT_SPACING),
  },
  pageBlock: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  },
  pageHeader: {
    display: 'grid',
    // Tight chevron so the title sits closer to the tree arrow.
    gridTemplateColumns: `${PAGE_ROW_GUTTER_PX}px minmax(0, 1fr) auto`,
    // Cancel the list bleed so the row actions line up with the section header
    // ones (Add / options) above.
    paddingRight: theme.spacing(PANEL_CONTENT_DEFAULT_SPACING),
    alignItems: 'center',
    minHeight: 40,
    transition: 'background-color 0.12s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  pageExpandCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
    minHeight: 40,
  },
  pageRowMain: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    minHeight: 40,
  },
  pageActionCell: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    minHeight: 40,
    // Same geometry as `sectionHeaderActions` so the columns line up.
    gap: theme.spacing(0.25),
  },
  pageRowHidden: {
    opacity: 0.55,
  },
  pageRowLabel: {
    // File name + page can outgrow the panel — truncate instead of wrapping.
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.body2.fontWeight,
    lineHeight: theme.typography.body2.lineHeight,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  pageDetailExpand: {
    display: 'grid',
    gridTemplateRows: '0fr',
    transition: 'grid-template-rows 200ms ease',
  },
  pageDetailExpandOpen: {
    gridTemplateRows: '1fr',
  },
  pageDetailExpandInner: {
    minHeight: 0,
    overflow: 'hidden',
  },
  thumbPlaceholder: {
    display: 'block',
    width: '100%',
    height: 160,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[100],
  },
  thumbImage: {
    display: 'block',
    width: '100%',
    maxHeight: 160,
    objectFit: 'contain',
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
  },
}));
